/** Enumerates the formats supported by the `DateConverters` object. */
export type SupportedDateFormat = 'YYYY-MM-DD' | 'YYYY-MM-DD hh:mmA' | 'MM/YY';

/**
 * Provides conversion methods for various date formats. Index using a `SupportedDateFormat` to
 * get a proxy object to conversion methods.
 */
export const DateConverters: {
  [key in SupportedDateFormat]: { toDate: (dateString: string) => Date | undefined; toString: (date: Date) => string };
} = {
  'YYYY-MM-DD hh:mmA': {
    toDate: dateString => {
      if (dateString.length !== 18) {
        // needs to be exactly 17 chars
        return undefined;
      }

      const [parsedDate, parsedTime] = dateString.split(' ');
      const [year, month, day] = parsedDate.split('-').filter(value => value !== '-');
      const [hourStr, minuteAStr] = parsedTime.split(':'); // minute with A (A is format for AM/PM)
      const minute = minuteAStr.substr(0, 2);
      const period = minuteAStr.substring(2, 4);

      let hour: number;
      if (period.toUpperCase() === 'AM') {
        hour = Number(hourStr) < 12 && Number(hourStr) > 0 ? Number(hourStr) : 0;
      } else if (period.toUpperCase() === 'PM') {
        hour = Number(hourStr) < 12 && Number(hourStr) > 0 ? Number(hourStr) + 12 : 12;
      } else {
        return undefined;
      }

      const date = new Date();
      date.setFullYear(Number(year), Number(month) - 1, Number(day));
      date.setHours(Number(hour), Number(minute));

      return date;
    },
    toString: date => {
      const exactMonth = date.getMonth() + 1;
      const month = exactMonth < 10 ? `0${exactMonth}` : exactMonth;
      const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
      const minute = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
      const hour = date.getHours();

      // 12-hour format
      let hour12: number;
      let period: 'AM' | 'PM';

      if (hour >= 0 && hour < 12) {
        // AM
        period = 'AM';
        hour12 = hour === 0 ? 12 : hour;
      } else {
        // PM
        hour12 = hour === 12 ? 12 : hour - 12;
        period = 'PM';
      }

      // 12-hour format but with 0 fillers
      const hour00: string = hour12 < 10 ? `0${hour12}` : `${hour12}`;

      return `${date.getFullYear()}-${month}-${day} ${hour00}:${minute}${period}`;
    },
  },
  'YYYY-MM-DD': {
    toDate: dateString => {
      if (dateString.length < 8 || dateString.length > 10) {
        return undefined;
      }
      const [year, month, day] = dateString.split('-').filter(value => value !== '-');
      const date = new Date();
      date.setFullYear(Number(year), Number(month) - 1, Number(day));
      return date;
    },
    toString: date => {
      const exactMonth = date.getMonth() + 1;
      const month = exactMonth < 10 ? `0${exactMonth}` : exactMonth;
      const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
      return `${date.getFullYear()}-${month}-${day}`;
    },
  },
  /** Assumes year is 20YY. */
  'MM/YY': {
    toDate: dateString => {
      if (dateString.length < 4 || dateString.length > 5) {
        return undefined;
      }
      const [month, year] = dateString.split('/').filter(value => value !== '/');
      const date = new Date();
      date.setFullYear(Number('20' + year), Number(month) - 1);
      return date;
    },
    toString: date => {
      return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
    },
  },
};
